import { useState, useMemo, useEffect } from 'react';
import { useAuth } from 'zite-auth-sdk';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Save, Printer, FolderOpen, ChevronLeft, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { saveGpCalculation, getMyToolsData, GetMyToolsDataOutputType } from 'zite-endpoints-sdk';
import CocktailCalc from '@/components/tools/CocktailCalc';

type TabType = 'beer' | 'wine' | 'spirit' | 'cocktail' | 'general';
type SavedCalc = GetMyToolsDataOutputType['gpCalculations'][0];

const f = (n: number) => `£${isNaN(n) || !isFinite(n) ? '0.00' : n.toFixed(2)}`;

function calcGP(costEx: number, sellInc: number) {
  if (!costEx || !sellInc || costEx <= 0 || sellInc <= 0) return null;
  const sellEx = sellInc / 1.2;
  const gpP = ((sellEx - costEx) / sellEx) * 100;
  return {
    costExVat: costEx, costIncVat: costEx * 1.2,
    sellIncVat: sellInc, sellExVat: sellEx,
    vatElement: sellInc - sellEx, gpPounds: sellEx - costEx, gpPct: gpP,
    suggested: [60, 65, 70].map(gp => ({ gp, exVat: costEx / (1 - gp / 100), incVat: (costEx / (1 - gp / 100)) * 1.2 })),
  };
}

function Results({ r }: { r: NonNullable<ReturnType<typeof calcGP>> }) {
  return (
    <div className="bg-muted/40 rounded-xl border border-border p-4 space-y-3">
      <h3 className="font-semibold text-xs uppercase tracking-wider text-muted-foreground">Results</h3>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
        <span className="text-muted-foreground">Cost price (ex VAT):</span><span className="font-medium">{f(r.costExVat)}</span>
        <span className="text-muted-foreground">Cost price (inc VAT):</span><span>{f(r.costIncVat)}</span>
        <span className="text-muted-foreground">Selling price (inc VAT):</span><span>{f(r.sellIncVat)}</span>
        <span className="text-muted-foreground">Selling price (ex VAT):</span><span>{f(r.sellExVat)}</span>
        <span className="text-muted-foreground">VAT element:</span><span>{f(r.vatElement)}</span>
        <span className="text-muted-foreground font-medium border-t border-border pt-1.5">GP £:</span>
        <span className={`font-bold border-t border-border pt-1.5 ${r.gpPounds >= 0 ? 'text-green-600 dark:text-green-400' : 'text-destructive'}`}>{f(r.gpPounds)}</span>
        <span className="text-muted-foreground font-medium">GP %:</span>
        <span className={`font-bold text-2xl ${r.gpPct >= 65 ? 'text-green-600 dark:text-green-400' : r.gpPct >= 55 ? 'text-yellow-600 dark:text-yellow-400' : 'text-destructive'}`}>{r.gpPct.toFixed(1)}%</span>
      </div>
      <div className="border-t border-border pt-3">
        <p className="text-xs text-muted-foreground mb-2 font-medium">Suggested selling prices (inc VAT):</p>
        <div className="grid grid-cols-3 gap-2">
          {r.suggested.map(s => (
            <div key={s.gp} className="text-center bg-background rounded-lg p-2.5 border border-border">
              <div className="text-xs text-muted-foreground">@{s.gp}% GP</div>
              <div className="font-bold text-sm">{f(s.incVat)}</div>
              <div className="text-xs text-muted-foreground">ex: {f(s.exVat)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const Placeholder = () => (
  <div className="text-center text-muted-foreground py-16 border border-dashed border-border rounded-xl text-sm">Enter values to see calculations</div>
);

export default function GpCalculator() {
  const { user } = useAuth();
  const [tab, setTab] = useState<TabType>('beer');
  const [saveDialog, setSaveDialog] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState<SavedCalc[]>([]);
  const [beer, setBeer] = useState({ name: '', litres: '30', cost: '', yield_: '88', sellInc: '', notes: '' });
  const [wine, setWine] = useState({ name: '', bottleCost: '', glassSize: '175', sellInc: '', notes: '' });
  const [spirit, setSpirit] = useState({ name: '', bottleSize: '700', bottleCost: '', measureSize: '25', sellInc: '', notes: '' });
  const [general, setGeneral] = useState({ name: '', costExVat: '', sellInc: '', notes: '' });

  useEffect(() => {
    if (!user) return;
    getMyToolsData({}).then(d => setSaved(d.gpCalculations.filter(c => c.status !== 'Archived')));
  }, [user]);

  const beerResult = useMemo(() => {
    const l = parseFloat(beer.litres) || 0, cost = parseFloat(beer.cost) || 0;
    const y = (parseFloat(beer.yield_) || 88) / 100, sell = parseFloat(beer.sellInc) || 0;
    if (!l || !cost || !sell) return null;
    return calcGP(cost / (l * 1.7598 * y), sell);
  }, [beer]);

  const wineResult = useMemo(() => {
    const bCost = parseFloat(wine.bottleCost) || 0, gSize = parseFloat(wine.glassSize) || 175;
    const sell = parseFloat(wine.sellInc) || 0;
    return bCost && sell ? calcGP(bCost / (750 / gSize), sell) : null;
  }, [wine]);

  const spiritResult = useMemo(() => {
    const bSize = parseFloat(spirit.bottleSize) || 700, bCost = parseFloat(spirit.bottleCost) || 0;
    const mSize = parseFloat(spirit.measureSize) || 25, sell = parseFloat(spirit.sellInc) || 0;
    return bCost && sell ? calcGP(bCost / (bSize / mSize), sell) : null;
  }, [spirit]);

  const generalResult = useMemo(() => {
    const cost = parseFloat(general.costExVat) || 0, sell = parseFloat(general.sellInc) || 0;
    return cost && sell ? calcGP(cost, sell) : null;
  }, [general]);

  const loadSaved = async () => {
    const d = await getMyToolsData({});
    setSaved(d.gpCalculations.filter(c => c.status !== 'Archived'));
  };

  const handleSave = async () => {
    if (!saveName.trim()) return;
    setIsSaving(true);
    try {
      const typeMap: Record<TabType, string> = { beer: 'Beer/Draught', wine: 'Wine', spirit: 'Spirit', cocktail: 'Cocktail', general: 'General' };
      const dataMap: Record<TabType, object> = { beer, wine, spirit, general, cocktail: {} };
      await saveGpCalculation({ name: saveName, type: typeMap[tab] as any, data: JSON.stringify(dataMap[tab]), status: 'Saved' });
      toast.success('Calculation saved!');
      setSaveDialog(false);
      await loadSaved();
    } catch { toast.error('Failed to save'); } finally { setIsSaving(false); }
  };

  const loadCalc = (calc: SavedCalc) => {
    try {
      const data = JSON.parse(calc.data);
      if (calc.type === 'Beer/Draught') { setTab('beer'); setBeer(data); }
      else if (calc.type === 'Wine') { setTab('wine'); setWine(data); }
      else if (calc.type === 'Spirit') { setTab('spirit'); setSpirit(data); }
      else if (calc.type === 'General') { setTab('general'); setGeneral(data); }
      else if (calc.type === 'Cocktail') setTab('cocktail');
      toast.success('Loaded: ' + calc.name);
    } catch { toast.error('Could not load'); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      <div className="flex items-center gap-3 mb-6">
        <Link to="/tools" className="text-muted-foreground hover:text-foreground"><ChevronLeft className="w-5 h-5" /></Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">GP Calculator</h1>
          <p className="text-sm text-muted-foreground">Calculate gross profit for any product type</p>
        </div>
        <a href="https://app.zite.so/preview/29oocs3fdn" target="_blank" rel="noreferrer" className="hidden sm:flex items-center gap-1.5 text-xs text-primary border border-primary/30 rounded-md px-2.5 py-1.5 hover:bg-primary/5 transition-colors">
          <ExternalLink className="w-3 h-3" /> POS Pricelist
        </a>
        <Sheet>
          <SheetTrigger asChild>
            <Button variant="outline" size="sm"><FolderOpen className="w-4 h-4 mr-1.5" /> Saved ({saved.length})</Button>
          </SheetTrigger>
          <SheetContent>
            <SheetHeader><SheetTitle>Saved Calculations</SheetTitle></SheetHeader>
            <div className="mt-4 space-y-2">
              {saved.length === 0 ? <p className="text-sm text-muted-foreground text-center py-8">No saved calculations yet</p> :
                saved.map(c => (
                  <div key={c.id} className="p-3 border border-border rounded-xl hover:bg-muted/50 cursor-pointer" onClick={() => loadCalc(c)}>
                    <p className="font-medium text-sm">{c.name}</p>
                    <p className="text-xs text-muted-foreground">{c.type} · {c.createdAt ? new Date(c.createdAt).toLocaleDateString('en-GB') : ''}</p>
                  </div>
                ))}
            </div>
          </SheetContent>
        </Sheet>
        <Button variant="outline" size="sm" onClick={() => window.print()}><Printer className="w-4 h-4 mr-1.5" /> Print</Button>
        <Button size="sm" onClick={() => { setSaveName((tab === 'beer' ? beer.name : tab === 'wine' ? wine.name : tab === 'spirit' ? spirit.name : general.name) || ''); setSaveDialog(true); }}>
          <Save className="w-4 h-4 mr-1.5" /> Save
        </Button>
      </div>

      <Tabs value={tab} onValueChange={v => setTab(v as TabType)}>
        <TabsList className="grid grid-cols-5 mb-6">
          <TabsTrigger value="beer">🍺 Beer</TabsTrigger>
          <TabsTrigger value="wine">🍷 Wine</TabsTrigger>
          <TabsTrigger value="spirit">🥃 Spirit</TabsTrigger>
          <TabsTrigger value="cocktail">🍹 Cocktail</TabsTrigger>
          <TabsTrigger value="general">📦 General</TabsTrigger>
        </TabsList>

        <TabsContent value="beer">
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="p-5 space-y-3">
              <h3 className="font-semibold">Beer / Draught Calculator</h3>
              <div><Label>Product Name</Label><Input className="mt-1" value={beer.name} onChange={e => setBeer(p => ({...p, name: e.target.value}))} placeholder="e.g. Guinness" /></div>
              <div><Label>Container Volume (litres)</Label><Input className="mt-1" type="number" value={beer.litres} onChange={e => setBeer(p => ({...p, litres: e.target.value}))} placeholder="30" /><p className="text-xs text-muted-foreground mt-0.5">Common: 20L, 30L, 41L (9G keg), 50L (11G keg)</p></div>
              <div><Label>Container Cost (£ ex VAT)</Label><Input className="mt-1" type="number" value={beer.cost} onChange={e => setBeer(p => ({...p, cost: e.target.value}))} placeholder="0.00" /></div>
              <div><Label>Yield % <span className="font-normal text-muted-foreground">(allow for froth/waste)</span></Label><Input className="mt-1" type="number" value={beer.yield_} onChange={e => setBeer(p => ({...p, yield_: e.target.value}))} /></div>
              {parseFloat(beer.litres) > 0 && <p className="text-xs text-muted-foreground">{(parseFloat(beer.litres) * 1.7598).toFixed(1)} gross pints → {((parseFloat(beer.litres) * 1.7598) * ((parseFloat(beer.yield_) || 88) / 100)).toFixed(1)} sellable pints</p>}
              <div><Label>Selling Price Per Pint (£ inc VAT)</Label><Input className="mt-1" type="number" value={beer.sellInc} onChange={e => setBeer(p => ({...p, sellInc: e.target.value}))} placeholder="0.00" /></div>
              <div><Label>Notes</Label><Textarea className="mt-1" value={beer.notes} onChange={e => setBeer(p => ({...p, notes: e.target.value}))} rows={2} /></div>
            </Card>
            <div>{beerResult ? <Results r={beerResult} /> : <Placeholder />}</div>
          </div>
        </TabsContent>

        <TabsContent value="wine">
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="p-5 space-y-3">
              <h3 className="font-semibold">Wine Calculator</h3>
              <div><Label>Product Name</Label><Input className="mt-1" value={wine.name} onChange={e => setWine(p => ({...p, name: e.target.value}))} placeholder="e.g. Sauvignon Blanc" /></div>
              <div><Label>Bottle Cost (£ ex VAT per 75cl)</Label><Input className="mt-1" type="number" value={wine.bottleCost} onChange={e => setWine(p => ({...p, bottleCost: e.target.value}))} placeholder="0.00" /></div>
              <div>
                <Label>Glass Size</Label>
                <Select value={wine.glassSize} onValueChange={v => setWine(p => ({...p, glassSize: v}))}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="125">125ml (6 glasses)</SelectItem>
                    <SelectItem value="175">175ml (4.3 glasses)</SelectItem>
                    <SelectItem value="250">250ml (3 glasses)</SelectItem>
                    <SelectItem value="500">500ml Carafe</SelectItem>
                  </SelectContent>
                </Select>
                {wine.bottleCost && <p className="text-xs text-muted-foreground mt-0.5">{(750 / (parseFloat(wine.glassSize) || 175)).toFixed(1)} glasses · Cost/glass: £{(parseFloat(wine.bottleCost) / (750 / (parseFloat(wine.glassSize) || 175))).toFixed(2)}</p>}
              </div>
              <div><Label>Selling Price Per Glass (£ inc VAT)</Label><Input className="mt-1" type="number" value={wine.sellInc} onChange={e => setWine(p => ({...p, sellInc: e.target.value}))} placeholder="0.00" /></div>
              <div><Label>Notes</Label><Textarea className="mt-1" value={wine.notes} onChange={e => setWine(p => ({...p, notes: e.target.value}))} rows={2} /></div>
            </Card>
            <div>{wineResult ? <Results r={wineResult} /> : <Placeholder />}</div>
          </div>
        </TabsContent>

        <TabsContent value="spirit">
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="p-5 space-y-3">
              <h3 className="font-semibold">Spirit Calculator</h3>
              <div><Label>Product Name</Label><Input className="mt-1" value={spirit.name} onChange={e => setSpirit(p => ({...p, name: e.target.value}))} placeholder="e.g. Hendrick's Gin" /></div>
              <div>
                <Label>Bottle Size</Label>
                <Select value={spirit.bottleSize} onValueChange={v => setSpirit(p => ({...p, bottleSize: v}))}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="700">70cl (700ml)</SelectItem>
                    <SelectItem value="1000">1 Litre (1000ml)</SelectItem>
                    <SelectItem value="500">50cl (500ml)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Bottle Cost (£ ex VAT)</Label><Input className="mt-1" type="number" value={spirit.bottleCost} onChange={e => setSpirit(p => ({...p, bottleCost: e.target.value}))} placeholder="0.00" /></div>
              <div>
                <Label>Measure Size</Label>
                <Select value={spirit.measureSize} onValueChange={v => setSpirit(p => ({...p, measureSize: v}))}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="25">25ml (standard single)</SelectItem>
                    <SelectItem value="35">35ml (large single)</SelectItem>
                    <SelectItem value="50">50ml (double)</SelectItem>
                  </SelectContent>
                </Select>
                {spirit.bottleCost && <p className="text-xs text-muted-foreground mt-0.5">{((parseFloat(spirit.bottleSize) || 700) / (parseFloat(spirit.measureSize) || 25)).toFixed(0)} measures · Cost: £{(parseFloat(spirit.bottleCost) / ((parseFloat(spirit.bottleSize) || 700) / (parseFloat(spirit.measureSize) || 25))).toFixed(2)}</p>}
              </div>
              <div><Label>Selling Price Per Measure (£ inc VAT)</Label><Input className="mt-1" type="number" value={spirit.sellInc} onChange={e => setSpirit(p => ({...p, sellInc: e.target.value}))} placeholder="0.00" /></div>
              <div><Label>Notes</Label><Textarea className="mt-1" value={spirit.notes} onChange={e => setSpirit(p => ({...p, notes: e.target.value}))} rows={2} /></div>
            </Card>
            <div>{spiritResult ? <Results r={spiritResult} /> : <Placeholder />}</div>
          </div>
        </TabsContent>

        <TabsContent value="cocktail">
          <Card className="p-5">
            <CocktailCalc onSave={async (data, name) => {
              try {
                await saveGpCalculation({ name, type: 'Cocktail', data: JSON.stringify(data), status: 'Saved' });
                toast.success('Cocktail spec saved!');
                await loadSaved();
              } catch { toast.error('Failed to save'); }
            }} />
          </Card>
        </TabsContent>

        <TabsContent value="general">
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="p-5 space-y-3">
              <h3 className="font-semibold">General Product Calculator</h3>
              <div><Label>Product Name</Label><Input className="mt-1" value={general.name} onChange={e => setGeneral(p => ({...p, name: e.target.value}))} placeholder="e.g. Soft Drink" /></div>
              <div><Label>Cost Price (£ ex VAT)</Label><Input className="mt-1" type="number" value={general.costExVat} onChange={e => setGeneral(p => ({...p, costExVat: e.target.value}))} placeholder="0.00" /></div>
              <div><Label>Selling Price (£ inc VAT at 20%)</Label><Input className="mt-1" type="number" value={general.sellInc} onChange={e => setGeneral(p => ({...p, sellInc: e.target.value}))} placeholder="0.00" /></div>
              <div><Label>Notes</Label><Textarea className="mt-1" value={general.notes} onChange={e => setGeneral(p => ({...p, notes: e.target.value}))} rows={2} /></div>
            </Card>
            <div>{generalResult ? <Results r={generalResult} /> : <Placeholder />}</div>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={saveDialog} onOpenChange={setSaveDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Save Calculation</DialogTitle></DialogHeader>
          <div className="py-2"><Label>Calculation Name</Label><Input className="mt-1.5" value={saveName} onChange={e => setSaveName(e.target.value)} placeholder="e.g. Guinness GP May 2026" /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveDialog(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={!saveName.trim() || isSaving}>{isSaving ? 'Saving...' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
